import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  useTheme,
  MenuItem,
  Divider,
  Paper,
  Stack,
  Chip,
} from "@mui/material";
import {
  AccountBalanceOutlined,
  PhoneIphoneOutlined,
  CheckCircleOutline,
  LockOutlined,
  SendOutlined,
  InfoOutlined,
  ReceiptOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import { setupManagementUrl, walletGraphqlUrl } from "../../config";
import {
  fetchJsonDynamic,
  executeAnyMutation,
  parseErrorMessage,
} from "../../data/DynamicServiceNew/jsondynamicservice";
import {
  walletQuery,
  WALLET_QUERY,
  OVERDRAFT_FACILITY_QUERY,
  CALCULATE_WITHDRAWAL_CHARGES_QUERY,
} from "../../data/WalletService/walletGraphQL";
import Swal from "sweetalert2";
import { formatKES } from "./WalletBalance";

const WalletWithdrawal = ({
  companyId,
  availableBalance = 0,
  onWithdrawalSubmitted,
  onClose,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  const [settlementMethod, setSettlementMethod] = useState("BANK"); // "BANK" or "MPESA"
  const [amount, setAmount] = useState("");
  const [bankCode, setBankCode] = useState("");
  const [banksList, setBanksList] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [bankAccountNumber, setBankAccountNumber] = useState("");
  const [accountName, setAccountName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [reason, setReason] = useState("");

  // Withdrawal charge breakdown from BE (calculateWithdrawalCharges)
  const [withdrawalCharges, setWithdrawalCharges] = useState({
    sababishaCharge: 0,
    mpesaCharge: 0,
    totalCharge: 0,
    tierId: null,
    scheduleId: null,
    isLoaded: false,
  });
  const [minRetainedBalance, setMinRetainedBalance] = useState(0);
  const [overdraftDebt, setOverdraftDebt] = useState(0);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);

  // Fetch banks setup dynamically directly from DB
  useEffect(() => {
    let isMounted = true;
    async function loadBanks() {
      setLoadingBanks(true);
      try {
        const res = await fetchJsonDynamic(
          "allBanks",
          null,
          null,
          setupManagementUrl.uri
        );
        if (res?.results && isMounted) {
          const parsed = JSON.parse(res.results);
          if (Array.isArray(parsed) && parsed.length > 0) {
            const mapped = parsed.map((b) => ({
              code: b.FinCode || b.BankSwiftCode || String(b.Id),
              name: b.BankName || `Bank #${b.Id}`,
            }));
            setBanksList(mapped);
            setBankCode(mapped[0]?.code || "");
          }
        }
      } catch (err) {
        console.error("Failed to load banks from setup:", err);
      } finally {
        if (isMounted) setLoadingBanks(false);
      }
    }
    loadBanks();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch wallet guardrails (minRetainedBalance, overdraftDebt)
  useEffect(() => {
    let isMounted = true;
    async function loadGuardrails() {
      if (!companyId) return;
      setLoadingConfig(true);
      try {
        // Query wallet for minRetainedBalance
        const wData = await walletQuery(WALLET_QUERY, { companyId: parseInt(companyId, 10) });
        if (wData?.wallet?.minimumRetainedBalance != null && isMounted) {
          setMinRetainedBalance(Number(wData.wallet.minimumRetainedBalance || 0));
        }
      } catch (te) {
        console.warn("Could not query wallet for minRetainedBalance:", te);
      }
      try {
        // Query overdraft facility for outstanding debt guard (Rule 3)
        const odData = await walletQuery(OVERDRAFT_FACILITY_QUERY, { companyId: parseInt(companyId, 10) });
        if (odData?.overdraftFacility && isMounted) {
          const od = odData.overdraftFacility;
          setOverdraftDebt(
            Number(od.outstandingPrincipal || 0) + Number(od.accruedInterest || 0)
          );
        }
      } catch (ode) {
        console.warn("Could not query overdraft for withdrawal guard:", ode);
      } finally {
        if (isMounted) setLoadingConfig(false);
      }
    }
    loadGuardrails();
    return () => { isMounted = false; };
  }, [companyId]);

  const numericAmount = parseFloat(amount) || 0;

  // Fetch withdrawal charges from BE whenever amount changes (tiered WithdrawalFeeSchedule)
  useEffect(() => {
    let isMounted = true;
    async function fetchCharges() {
      if (numericAmount <= 0) {
        setWithdrawalCharges({ sababishaCharge: 0, mpesaCharge: 0, totalCharge: 0, tierId: null, scheduleId: null, isLoaded: false });
        return;
      }
      try {
        const data = await walletQuery(CALCULATE_WITHDRAWAL_CHARGES_QUERY, { amount: numericAmount });
        if (data?.calculateWithdrawalCharges && isMounted) {
          const c = data.calculateWithdrawalCharges;
          setWithdrawalCharges({
            sababishaCharge: Number(c.sababishaCharge || 0),
            mpesaCharge: Number(c.mpesaCharge || 0),
            totalCharge: Number(c.totalCharges ?? c.totalCharge ?? 0),
            tierId: c.tierId ?? null,
            grossAmount: Number(c.grossAmount ?? numericAmount),
            netAmount: Number(c.netAmount ?? (numericAmount + Number(c.totalCharges ?? 0))),
            chargeType: c.chargeType || "FLAT",
            isLoaded: true,
          });
        }
      } catch (err) {
        console.warn("calculateWithdrawalCharges failed:", err);
      }
    }
    fetchCharges();
    return () => { isMounted = false; };
  }, [numericAmount]);

  // Breakdown: Net payout is what recipient gets, totalFee is charges, totalDeduction is amount + totalFee
  const totalFee = withdrawalCharges.totalCharge;
  const netPayout = numericAmount;
  const totalDeduction = numericAmount > 0 ? numericAmount + totalFee : 0;
  const balanceAfter = Math.max(0, availableBalance - totalDeduction);

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();

    if (!numericAmount || numericAmount <= 0) {
      Swal.fire({
        icon: "warning",
        title: "Invalid Amount",
        text: "Please enter a valid amount to withdraw.",
      });
      return;
    }

    // Rule 3 check: No overdraft outstanding (Section 8.5)
    if (overdraftDebt > 0) {
      Swal.fire({
        icon: "error",
        title: "Overdraft Outstanding (Rule 3)",
        text: `Withdrawal blocked. You have ${formatKES(overdraftDebt)} outstanding in overdraft principal/interest. Inflows must clear overdraft first before withdrawals.`,
      });
      return;
    }

    // Rule 4 check: Minimum retained balance (Section 8.5 & 11.4)
    const maxWithdrawable = Math.max(0, availableBalance - minRetainedBalance);
    if ((numericAmount + totalFee) > maxWithdrawable) {
      Swal.fire({
        icon: "error",
        title: "Retained Reserve Violated (Rule 4)",
        text: `Your available balance is ${formatKES(availableBalance)}, but a minimum continuity reserve of ${formatKES(minRetainedBalance)} must be retained. Maximum withdrawable amount (including ${formatKES(totalFee)} charges) is ${formatKES(maxWithdrawable)}.`,
      });
      return;
    }

    if (settlementMethod === "BANK") {
      if (!bankAccountNumber || bankAccountNumber.length < 5) {
        Swal.fire({
          icon: "warning",
          title: "Bank Details Incomplete",
          text: "Please provide a valid destination bank account number.",
        });
        return;
      }
    } else {
      let cleanPhone = phoneNumber.replace(/\D/g, "");
      if (cleanPhone.length < 9) {
        Swal.fire({
          icon: "warning",
          title: "Phone Required",
          text: "Please provide a valid M-Pesa phone number for payout.",
        });
        return;
      }
    }

    const swalOnTop = {
      didOpen: () => {
        const el = document.querySelector(".swal2-container");
        if (el) el.style.zIndex = "2000";
      },
    };

    setSubmitting(true);

    try {
      // 1. Request OTP to be sent to requester's email
      Swal.fire({
        title: "Sending OTP...",
        text: "Please wait while your verification code is being generated.",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const el = document.querySelector(".swal2-container");
          if (el) el.style.zIndex = "2000";
        },
      });

      const otpRequest = await executeAnyMutation(
        "requestWithdrawalOtp",
        { actionType: "Withdrawal Request" },
        null,
        false,
        walletGraphqlUrl.uri
      );

      if (!otpRequest || otpRequest?.error) {
        const raw = otpRequest?.error || "Unable to contact the server. Please try again.";
        const errMsg = (() => {
          try { return JSON.parse(raw)?.message || raw; }
          catch { return raw; }
        })();
        console.error("[RequestWithdrawalOtp] Error:", raw);
        Swal.fire({ icon: "error", title: "OTP Error", text: errMsg, ...swalOnTop });
        setSubmitting(false);
        return;
      }

      let maskedEmail = "";
      try {
        const parsed = typeof otpRequest.results === "string" ? JSON.parse(otpRequest.results) : otpRequest.results;
        maskedEmail = parsed?.maskedEmail || "";
      } catch (err) {
        console.warn("Could not parse maskedEmail:", err);
      }

      const emailNotice = maskedEmail
        ? `An OTP has been sent to your registered email (<b>${maskedEmail}</b>).`
        : "An OTP has been sent to your registered email address.";

      // 2. Prompt for OTP with inline 30s cooldown and resend
      let timerInterval = null;
      let secondsLeft = 30;

      const otpPrompt = await Swal.fire({
        title: "Enter Withdrawal Verification OTP",
        html: `
          <div style="font-size: 14px; color: #555; margin-bottom: 8px;">
            ${emailNotice}<br/>Enter the 6-digit code to submit your withdrawal request.
          </div>
          <div style="font-size: 12px; font-weight: 700; color: #1766a2; margin: 12px auto 4px auto; letter-spacing: 0.5px;">
            ENTER 6-DIGIT CODE:
          </div>
          <input
            id="swal-otp-input"
            type="text"
            inputmode="numeric"
            autocomplete="one-time-code"
            placeholder="· · · · · ·"
            style="
              background: #ffffff !important;
              border: 2px solid #1766a2 !important;
              color: #0f172a !important;
              text-align: center;
              font-size: 26px;
              letter-spacing: 10px;
              font-weight: 800;
              margin: 4px auto 12px auto;
              width: 220px;
              height: 48px;
              border-radius: 8px;
              box-shadow: 0 0 0 3px rgba(23, 102, 162, 0.15);
              outline: none;
              display: block;
              box-sizing: border-box;
              cursor: text;
            "
          />
          <div style="margin-top: 8px; font-size: 0.875rem;">
            <span id="otp-timer-box" style="color: #64748b;">
              Request another OTP in <b id="otp-countdown" style="color: #0284c7;">30</b>s
            </span>
            <button id="swal-resend-btn" type="button" style="display: none; background: none; border: none; color: #1766A2; font-weight: 700; cursor: pointer; text-decoration: underline; font-size: 0.9rem;">
              Resend OTP
            </button>
          </div>
          <div id="otp-status-msg" style="margin-top: 8px; font-size: 0.82rem; min-height: 18px;"></div>
        `,
        icon: "info",
        showCancelButton: true,
        confirmButtonText: "Verify & Submit",
        confirmButtonColor: "#1766A2",
        cancelButtonText: "Cancel",
        focusConfirm: false,
        didOpen: () => {
          const el = document.querySelector(".swal2-container");
          if (el) el.style.zIndex = "2000";

          const input = document.getElementById("swal-otp-input");
          if (input) {
            setTimeout(() => input.focus(), 50);

            input.addEventListener("input", (e) => {
              e.target.value = e.target.value.replace(/\D/g, "").slice(0, 6);
            });

            input.addEventListener("paste", (e) => {
              e.preventDefault();
              const text = (e.clipboardData || window.clipboardData).getData("text");
              e.target.value = text.replace(/\D/g, "").slice(0, 6);
            });
          }

          const timerBox = document.getElementById("otp-timer-box");
          const countdownEl = document.getElementById("otp-countdown");
          const resendBtn = document.getElementById("swal-resend-btn");
          const statusMsg = document.getElementById("otp-status-msg");

          const updateTimer = () => {
            if (secondsLeft > 0) {
              secondsLeft -= 1;
              if (countdownEl) countdownEl.innerText = String(secondsLeft);
            }
            if (secondsLeft <= 0) {
              clearInterval(timerInterval);
              if (timerBox) timerBox.style.display = "none";
              if (resendBtn) resendBtn.style.display = "inline-block";
            }
          };

          timerInterval = setInterval(updateTimer, 1000);

          if (resendBtn) {
            resendBtn.onclick = async () => {
              resendBtn.disabled = true;
              resendBtn.style.opacity = "0.5";
              if (statusMsg) {
                statusMsg.style.color = "#0284c7";
                statusMsg.innerText = "Requesting new OTP...";
              }
              try {
                const resendRes = await executeAnyMutation(
                  "requestWithdrawalOtp",
                  { actionType: "Withdrawal Request" },
                  null,
                  false,
                  walletGraphqlUrl.uri
                );
                if (resendRes?.error) {
                  const errM = (() => {
                    try { return JSON.parse(resendRes.error)?.message || resendRes.error; }
                    catch { return resendRes.error || "Failed to resend OTP."; }
                  })();
                  if (statusMsg) {
                    statusMsg.style.color = "#dc2626";
                    statusMsg.innerText = errM;
                  }
                  resendBtn.disabled = false;
                  resendBtn.style.opacity = "1";
                } else {
                  secondsLeft = 30;
                  if (countdownEl) countdownEl.innerText = "30";
                  if (timerBox) timerBox.style.display = "inline";
                  if (resendBtn) resendBtn.style.display = "none";
                  resendBtn.disabled = false;
                  resendBtn.style.opacity = "1";
                  if (statusMsg) {
                    statusMsg.style.color = "#16a34a";
                    statusMsg.innerText = "New OTP sent to your email!";
                  }
                  clearInterval(timerInterval);
                  timerInterval = setInterval(updateTimer, 1000);
                }
              } catch (err) {
                resendBtn.disabled = false;
                resendBtn.style.opacity = "1";
                if (statusMsg) {
                  statusMsg.style.color = "#dc2626";
                  statusMsg.innerText = err.message || "Failed to resend OTP.";
                }
              }
            };
          }
        },
        willClose: () => {
          if (timerInterval) clearInterval(timerInterval);
        },
        preConfirm: async () => {
          const val = document.getElementById("swal-otp-input")?.value?.trim();
          if (!val) {
            Swal.showValidationMessage("OTP code is required.");
            return false;
          }
          if (!/^\d{6}$/.test(val)) {
            Swal.showValidationMessage("OTP must be a 6-digit number.");
            return false;
          }

          try {
            const validateRes = await executeAnyMutation(
              "validateWithdrawalOtp",
              { otp: val },
              null,
              false,
              walletGraphqlUrl.uri
            );
            if (validateRes?.error) {
              const errMsg = (() => {
                try { return JSON.parse(validateRes.error)?.message || validateRes.error; }
                catch { return validateRes.error || "Invalid OTP code entered."; }
              })();
              Swal.showValidationMessage(errMsg);
              return false;
            }
            return val;
          } catch (err) {
            Swal.showValidationMessage(err.message || "OTP verification failed.");
            return false;
          }
        },
      });

      if (!otpPrompt.isConfirmed || !otpPrompt.value) {
        setSubmitting(false);
        return;
      }

      const finalOtp = otpPrompt.value;

      // 4. Submit withdrawal mutation after OTP verification
      Swal.fire({
        title: "Submitting Withdrawal...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
          const el = document.querySelector(".swal2-container");
          if (el) el.style.zIndex = "2000";
        },
      });

      // insertWalletWitdrawal: p_action 0 = request a withdrawal
      const response = await executeAnyMutation(
        "insertWalletWitdrawal",
        {
          p_phone_number: phoneNumber.replace(/\D/g, ""),
          p_amount: numericAmount,
          p_narration: reason || "Company wallet withdrawal",
          p_action: 0,
          p_otp: finalOtp,
        },
        "wallet",
        false,
        walletGraphqlUrl.uri
      );

      if (!response || response.error) {
        throw new Error(parseErrorMessage(response?.error, "Withdrawal"));
      }

      let extractedId = null;
      try {
        let parsed = response;
        if (parsed?.results !== undefined) parsed = parsed.results;
        if (typeof parsed === "string") {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            if (/^\d+$/.test(parsed.trim())) extractedId = parsed.trim();
          }
        }
        if (Array.isArray(parsed)) parsed = parsed[0];
        if (typeof parsed === "number" || (typeof parsed === "string" && /^\d+$/.test(String(parsed).trim()))) {
          extractedId = String(parsed).trim();
        } else if (parsed && typeof parsed === "object") {
          extractedId =
            parsed.RequestId ??
            parsed.requestId ??
            parsed.Id ??
            parsed.id ??
            parsed.withdrawal_id ??
            parsed.WithdrawalId ??
            parsed.insertwalletwithdrawal ??
            parsed.Insertwalletwithdrawal ??
            null;
          if (!extractedId) {
            const numericVal = Object.values(parsed).find(
              (v) => typeof v === "number" || (typeof v === "string" && /^\d+$/.test(v))
            );
            if (numericVal !== undefined && numericVal !== null) {
              extractedId = String(numericVal);
            }
          }
        }
      } catch (parseErr) {
        console.warn("Could not parse requestId directly from response:", parseErr);
      }

      // Fallback query if requestId was not directly extractable from mutation response
      if (!extractedId) {
        try {
          const cleanPhone = phoneNumber.replace(/\D/g, "");
          const latestList = await fetchJsonDynamic(
            "walletWithdrawal",
            "wallet",
            { iscancelled: 0 },
            walletGraphqlUrl.uri
          );
          let rows = [];
          if (latestList?.results) {
            const parsed =
              typeof latestList.results === "string"
                ? JSON.parse(latestList.results)
                : latestList.results;
            rows = Array.isArray(parsed) ? parsed : [];
          }
          const matched = rows
            .filter((r) => String(r.phone_number || "").replace(/\D/g, "") === cleanPhone)
            .sort((a, b) => Number(b.id || 0) - Number(a.id || 0));
          if (matched.length > 0 && matched[0].id) {
            extractedId = matched[0].id;
          }
        } catch (queryErr) {
          console.warn("Fallback query for requestId failed:", queryErr);
        }
      }

      setSubmittedData({
        requestId: extractedId ?? null,
        grossAmount: numericAmount,
        totalFee,
        netAmount: netPayout,
        settlementMethod,
        status: "PENDING_APPROVAL",
      });

      await Swal.fire({
        icon: "success",
        title: "Withdrawal Submitted",
        text: `Request has been queued in PENDING_APPROVAL status.`,
        confirmButtonText: "OK",
        confirmButtonColor: "#5c67f5",
        ...swalOnTop,
      });

      if (onWithdrawalSubmitted) onWithdrawalSubmitted();
    } catch (err) {
      console.error("Withdrawal initiation failed:", err);
      const errMsg =
        err.response?.data?.message ||
        err.response?.data?.title ||
        err.response?.data ||
        err.message ||
        "Could not submit withdrawal request. Please check inputs and try again.";
      Swal.fire({
        icon: "error",
        title: "Request Failed",
        text: typeof errMsg === "string" ? errMsg : JSON.stringify(errMsg),
        ...swalOnTop,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetForm = () => {
    setSubmittedData(null);
    setAmount("");
    setBankAccountNumber("");
    setAccountName("");
    setPhoneNumber("");
    setReason("");
  };

  return (
    <Box sx={{ width: "100%" }}>
      {!submittedData ? (
        <Card
          sx={{
            borderRadius: 2.5,
            backgroundColor: isDark ? colors.primary[400] : "#ffffff",
            boxShadow: "none",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
          }}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 1 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                    Request Wallet Funds Withdrawal
                  </Typography>
                  <Typography variant="body2" sx={{ color: colors.grey[400], mt: 0.5 }}>
                    Transfer available funds from your centralized company wallet back to your bank account or M-Pesa.
                  </Typography>
                </Box>
                <Chip
                  icon={<LockOutlined sx={{ fontSize: "16px !important" }} />}
                  label="Maker-Checker Protected"
                  color="info"
                  variant="outlined"
                  size="small"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>

            {/* Maker-Checker Segregation Notice */}
            <Alert
              severity="info"
              icon={<InfoOutlined fontSize="inherit" />}
              sx={{
                mb: 3,
                borderRadius: 2,
                backgroundColor: isDark ? "rgba(31,118,173,0.12)" : "#e8f4fc",
                border: "1px solid rgba(31,118,173,0.3)",
              }}
            >
              <Typography variant="caption" sx={{ display: "block", color: isDark ? colors.grey[200] : "#0d3c61", lineHeight: 1.5 }}>
                <strong>Segregation of Duties (Maker-Checker):</strong> In compliance with financial governance controls, withdrawal requests are queued in <code>PENDING_APPROVAL</code>. A Sababisha verification checker reviews and authorizes the final payout before disbursement.
              </Typography>
            </Alert>

            <Grid container spacing={3}>
              {/* Form Input Columns */}
              <Grid item xs={12} md={7}>
                <Stack spacing={2.5}>
                  {/* Settlement Destination Type */}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colors.grey[200] }}>
                      Payout Destination *
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant={settlementMethod === "BANK" ? "contained" : "outlined"}
                          onClick={() => setSettlementMethod("BANK")}
                          startIcon={<AccountBalanceOutlined />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.2,
                            borderRadius: 2,
                            backgroundColor: settlementMethod === "BANK" ? "#1766A2" : "transparent",
                          }}
                        >
                          Bank Transfer
                        </Button>
                      </Grid>
                      <Grid item xs={6}>
                        <Button
                          fullWidth
                          variant={settlementMethod === "MPESA" ? "contained" : "outlined"}
                          onClick={() => setSettlementMethod("MPESA")}
                          startIcon={<PhoneIphoneOutlined />}
                          sx={{
                            textTransform: "none",
                            fontWeight: 600,
                            py: 1.2,
                            borderRadius: 2,
                            backgroundColor: settlementMethod === "MPESA" ? "#22bf72" : "transparent",
                          }}
                        >
                          M-Pesa Payout
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>

                  {/* Withdrawal Amount */}
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: colors.grey[200] }}>
                        Withdrawal Amount (Net Payout) (KES) *
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#1f76ad",
                          cursor: "pointer",
                          fontWeight: 600,
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => setAmount(String(availableBalance))}
                      >
                        Max Available: {formatKES(availableBalance)}
                      </Typography>
                    </Box>
                    <TextField
                      fullWidth
                      type="number"
                      placeholder="e.g. 50000"
                      value={amount}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || Number(val) >= 0) setAmount(val);
                      }}
                      inputProps={{ min: 0, step: "any" }}
                      error={totalDeduction > availableBalance || (amount !== "" && Number(amount) < 0)}
                      helperText={
                        amount !== "" && Number(amount) < 0
                          ? "Amount cannot be below 0"
                          : totalDeduction > availableBalance
                          ? `Total deduction (${formatKES(totalDeduction)}) exceeds spendable wallet balance`
                          : ""
                      }
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography sx={{ fontWeight: 700, color: colors.grey[400] }}>
                              KES
                            </Typography>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Box>

                  {/* Bank Transfer Details */}
                  {settlementMethod === "BANK" ? (
                    <>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colors.grey[200] }}>
                          Destination Bank *
                        </Typography>
                        <TextField
                          select
                          fullWidth
                          value={bankCode}
                          onChange={(e) => setBankCode(e.target.value)}
                          disabled={loadingBanks}
                        >
                          {banksList.length === 0 ? (
                            <MenuItem value="" disabled>
                              {loadingBanks ? "Loading banks from database..." : "No banks configured in Setup"}
                            </MenuItem>
                          ) : (
                            banksList.map((b) => (
                              <MenuItem key={b.code} value={b.code}>
                                {b.name} (Code: {b.code})
                              </MenuItem>
                            ))
                          )}
                        </TextField>
                      </Box>

                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colors.grey[200] }}>
                          Bank Account Number *
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="e.g. 01080123456789"
                          value={bankAccountNumber}
                          onChange={(e) => setBankAccountNumber(e.target.value)}
                        />
                      </Box>

                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colors.grey[200] }}>
                          Account Holder / Company Name (Optional)
                        </Typography>
                        <TextField
                          fullWidth
                          placeholder="e.g. Acme Microfinance Ltd"
                          value={accountName}
                          onChange={(e) => setAccountName(e.target.value)}
                        />
                      </Box>
                    </>
                  ) : (
                    /* M-Pesa Payout */
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colors.grey[200] }}>
                        Destination M-Pesa Phone Number *
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder="e.g. 0712345678 or 254712345678"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIphoneOutlined sx={{ color: colors.grey[400] }} />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Box>
                  )}

                  {/* Notes / Reason */}
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: colors.grey[200] }}>
                      Withdrawal Purpose / Internal Notes
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={2}
                      placeholder="e.g. Routine treasury rebalancing to operational account"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                    />
                  </Box>
                </Stack>
              </Grid>

              {/* Dynamic Fee & Net Payout Summary Column */}
              <Grid item xs={12} md={5}>
                <Paper
                  sx={{
                    p: 3,
                    borderRadius: 2.5,
                    backgroundColor: isDark ? "rgba(0,0,0,0.25)" : "#f8fafc",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <ReceiptOutlined sx={{ color: "#1f76ad" }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                        Withdrawal Breakdown
                      </Typography>
                    </Box>

                    <Divider sx={{ mb: 2 }} />

                    <Stack spacing={2}>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                          Net Payout:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                          {formatKES(netPayout)}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Box>
                          <Typography variant="body2" sx={{ color: colors.grey[300] }}>
                            Total Charges (Sababisha + Safaricom):
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                            Sababisha: {formatKES(withdrawalCharges.sababishaCharge)} &nbsp;|&nbsp; Safaricom: {formatKES(withdrawalCharges.mpesaCharge)}
                          </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                          + {formatKES(totalFee)}
                        </Typography>
                      </Box>

                      <Divider />

                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "baseline",
                          pt: 1,
                        }}
                      >
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.grey[100] }}>
                            Total Deduction:
                          </Typography>
                          <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                            Total deducted from funds available
                          </Typography>
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 800,
                            color: "#22bf72",
                            fontFamily: "Outfit, Inter, sans-serif",
                          }}
                        >
                          {formatKES(totalDeduction)}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                          border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
                          mt: 2,
                        }}
                      >
                        <Typography variant="caption" sx={{ color: colors.grey[400], display: "block" }}>
                          Wallet balance after submission:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: colors.grey[200] }}>
                          {formatKES(balanceAfter)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ mt: 3 }}>
                    <Button
                      fullWidth
                      variant="contained"
                      onClick={handleSubmit}
                      disabled={submitting || numericAmount <= 0 || (numericAmount + totalFee) > availableBalance}
                      startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <SendOutlined />}
                      sx={{
                        backgroundColor: "#1766A2",
                        color: "#ffffff",
                        py: 1.4,
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: 2,
                        boxShadow: "0 4px 14px rgba(23,102,162,0.3)",
                        "&:hover": { backgroundColor: "#0f4c7a" },
                      }}
                    >
                      {submitting ? "Submitting Request..." : "Submit Withdrawal for Approval"}
                    </Button>
                    {onClose && (
                      <Button
                        fullWidth
                        variant="text"
                        onClick={onClose}
                        sx={{ mt: 1, textTransform: "none", color: colors.grey[300] }}
                      >
                        Cancel
                      </Button>
                    )}
                  </Box>
                </Paper>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      ) : (
        /* Success / PENDING_APPROVAL Confirmation Card */
        <Card
          sx={{
            borderRadius: 2.5,
            backgroundColor: isDark ? colors.primary[400] : "#ffffff",
            boxShadow: "none",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e2e8f0"}`,
            textAlign: "center",
            p: { xs: 3, sm: 5 },
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: "50%",
              backgroundColor: "rgba(33, 150, 243, 0.12)",
              color: "#2196f3",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              mx: "auto",
              mb: 2.5,
            }}
          >
            <CheckCircleOutline sx={{ fontSize: 44 }} />
          </Box>

          <Chip
            label={submittedData.status || "PENDING_APPROVAL"}
            color="warning"
            sx={{ fontWeight: 700, mb: 1.5 }}
          />

          <Typography variant="h5" sx={{ fontWeight: 800, color: colors.grey[100], mb: 1 }}>
            Withdrawal Request Queued
          </Typography>

          <Typography variant="body1" sx={{ color: colors.grey[300], maxWidth: 520, mx: "auto", mb: 3 }}>
            Withdrawal request {submittedData.requestId ? <strong>#{submittedData.requestId} </strong> : ""}for{" "}
            <strong>{formatKES(submittedData.grossAmount)}</strong> has been registered.
          </Typography>

          <Paper
            sx={{
              maxWidth: 480,
              mx: "auto",
              p: 2.5,
              borderRadius: 2,
              backgroundColor: isDark ? "rgba(0,0,0,0.2)" : "#f8fafc",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "#e2e8f0"}`,
              textAlign: "left",
              mb: 3,
            }}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                  Withdrawal Fee Deducted:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatKES(submittedData.totalFee)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                  Expected Net Payout:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 700, color: "#22bf72" }}>
                  {formatKES(submittedData.netAmount)}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 0.5 }} />
                <Typography variant="caption" sx={{ color: colors.grey[400] }}>
                  Status Message:
                </Typography>
                <Typography variant="caption" sx={{ display: "block", color: colors.grey[200] }}>
                  Awaiting secondary sign-off. Once approved, the funds will transfer to your designated destination.
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="outlined"
              onClick={handleResetForm}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Request Another Withdrawal
            </Button>
            {onClose && (
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor: "#1766A2",
                  color: "#fff",
                  textTransform: "none",
                  fontWeight: 700,
                }}
              >
                Close & Return to Dashboard
              </Button>
            )}
          </Stack>
        </Card>
      )}
    </Box>
  );
};

export default WalletWithdrawal;
